import { useState, createContext, useContext, useCallback, useEffect } from 'react';
import { client } from '../helpers/api-client';
import { logmarLetterPool, logmarData } from '../helpers/data';
import { getRandomCharacters } from '../helpers/functions';
import { usePostMessage, useReceiveMessage } from './messageContext';

const TestContext = createContext();

export const useTestContext = () => {
  return useContext(TestContext);
};

export const TestProvider = ({ children }) => {
  const receivedMessage = useReceiveMessage();
  const postMessage = usePostMessage();
  const [testData, setTestData] = useState({
    forthValleyVisualFieldDots: [],
    maxPermittedLogmar: { id: 1, value: 1.0 },
    lettersToUse: 'sloan',
    testRightEye: false,
    testLeftEye: false,
    screenHeightMm: 340,
    cardWidthPx: 352,
    cardHeightPx: 220,
    cardWidthMm: 85,
    cardHeightMm: 54,
    imageWidthReference: 300,
    circleDimensionsReferenceMm: 4,
    imageWidthCalculated: 0,
    circleDimensionsMm: 0,
    eyeTestId: 1,
    fontAdjustmentPercentage: 125.5,
    testMode: 'auto',
    selectedOptimaSizes: {},
    testResultsId: '',
    testResults: {},
    visualFieldSettings: {
      screenBuffer: 0.05,
      fixationTargetDimensions: {
        width: 150,
        height: 100,
      },
    },
  });

  const [logmarCharacters, setLogmarCharacters] = useState({});
  const [contrastSensitivityData, setContrastSensitivityData] = useState({ settings: {}, content: {} });

  const setTestFieldValue = ({ name, value }) => {
    setTestData((testData) => ({ ...testData, [name]: value }));
  };

  const setMultipleTestFieldValues = (data) => {
    setTestData({ ...testData, ...data });
  };

  const generateLogmarCharacters = useCallback(() => {
    //this is used in open & crowded logmar exclusively
    const { allChars, numToDisplay } = logmarLetterPool[testData.lettersToUse];
    let generatedChars = [];
    while (generatedChars.length <= logmarData.length) {
      const chars = getRandomCharacters(allChars, numToDisplay);
      generatedChars.push(chars);
    }
    return setLogmarCharacters(generatedChars);
  }, [testData.lettersToUse]);

  const getTestResults = async (testId) => {
    client(`api/appointment-test-result/${testId}`).then(
      (data) => {
        setTestFieldValue({ name: 'testResults', value: data });
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const setContrastSensitivityConfig = (contrastSensitivityConfig) => {
    setContrastSensitivityData({ ...contrastSensitivityData, settings: contrastSensitivityConfig });
  };

  const setContrastSensitivityContent = (contrastSensitivityContent) => {
    setContrastSensitivityData({ ...contrastSensitivityData, content: contrastSensitivityContent });
  };

  const generateRandomCharacters = () => {
    //this is used in contrast sesnsitivity exclusively
    const { allChars } = logmarLetterPool['sloan'];
    const newGeneratedCharacters = getRandomCharacters(allChars, 3);
    return newGeneratedCharacters;
  };

  const updateTestData = useCallback((newData) => postMessage({ type: 'synchroniseTestData', content: newData }), [postMessage]);

  useEffect(() => {
    if (receivedMessage?.type !== 'synchroniseTestData') return;
    setTestData((existingData) => {
      return { ...existingData, ...receivedMessage.content };
    });
  }, [receivedMessage]);

  return (
    <TestContext.Provider
      value={{
        testData,
        setMultipleTestFieldValues,
        setTestFieldValue,
        setTestData,
        generateLogmarCharacters,
        getTestResults,
        logmarCharacters,
        generateRandomCharacters,
        setContrastSensitivityConfig,
        setContrastSensitivityContent,
        contrastSensitivityData,
        updateTestData,
      }}
    >
      {children}
    </TestContext.Provider>
  );
};
